<template>
  <CardBase :apagada="!algumaAtiva">
    <template #logo>
      <LogoIntegracao
        :logo="conector.logo"
        :nome="conector.nome"
        :icone="conector.icone || 'cil-puzzle'"
        tamanho="grande"
      />
    </template>

    <template #selos>
      <span class="grupo__contagem">
        {{ ativas.length }} de {{ conector.total_escopos || integracoes.length }}
      </span>
    </template>

    <template #nome>{{ conector.nome }}</template>
    <template #subtitulo>{{ conector.categoria_nome }}</template>

    <ul class="finalidades">
      <li
        v-for="integracao in ordenadas"
        :key="integracao.id"
        class="finalidade"
        :class="{ 'finalidade--inativa': !integracao.ativo }"
        role="button"
        tabindex="0"
        :title="'Configurar ' + (integracao.escopo_nome || integracao.nome)"
        @click="$emit('configurar', integracao)"
        @keyup.enter="$emit('configurar', integracao)"
      >
        <span class="finalidade__ponto" :style="{ background: corDoEstado(integracao) }"></span>
        <span class="finalidade__nome">{{ integracao.escopo_nome || integracao.nome }}</span>
        <span v-if="integracao.ativo && integracao.status_conexao === 'FALHA'" class="finalidade__aviso">
          <fa-icon icon="exclamation-triangle" /> falha
        </span>
        <span v-else-if="!integracao.ativo" class="finalidade__aviso">desativada</span>
      </li>
    </ul>

    <template #rodape>
      <CButton size="sm" color="primary" @click="$emit('configurar', principal)">
        <fa-icon icon="pencil-alt" /> Configurar
      </CButton>

      <!--
        Replicar mora dentro da configuração, que é onde se vê o que está sendo
        copiado. Aqui fica só o atalho, porque é da lista que se percebe que uma
        finalidade ficou para trás.
      -->
      <CButton size="sm" variant="outline" color="info" @click="$emit('replicar', principal)">
        <fa-icon icon="copy" /> Aplicar a outras
      </CButton>
    </template>
  </CardBase>
</template>

<script>
/*
 * Um conector cujas instâncias são finalidades da mesma coisa.
 *
 * O caso que pede isso é o servidor de e-mail: são oito configurações, uma por
 * tipo de envio, com os mesmos campos e, quase sempre, o mesmo servidor. Como
 * oito cartões, eles ocupavam quase toda a lista, empurravam para baixo as
 * outras integrações e faziam parecer que a empresa tem oito serviços de e-mail
 * diferentes.
 *
 * Reunidos, o cartão responde de relance o que interessa: quantas finalidades
 * já têm servidor, quais estão no ar e qual ficou para trás.
 */
import CardBase from './CardBase';
import LogoIntegracao from './LogoIntegracao';

export default {
  name: 'CardGrupoIntegracao',
  components: { CardBase, LogoIntegracao },
  props: {
    // Todas as instâncias configuradas do mesmo conector.
    integracoes: {
      type: Array,
      required: true,
    },
  },
  computed: {
    conector() {
      return (this.integracoes[0] || {}).conector || {};
    },

    /*
     * Na ordem em que o conector declara as finalidades, que é a do fluxo
     * ("Padrão" primeiro), e não a alfabética com que a lista chega.
     */
    ordenadas() {
      return [...this.integracoes].sort((uma, outra) => {
        const posicaoDeUma = uma.escopo_ordem === null ? 99 : uma.escopo_ordem;
        const posicaoDeOutra = outra.escopo_ordem === null ? 99 : outra.escopo_ordem;

        return posicaoDeUma - posicaoDeOutra;
      });
    },

    ativas() {
      return this.integracoes.filter((integracao) => integracao.ativo);
    },

    algumaAtiva() {
      return this.ativas.length > 0;
    },

    /*
     * A configuração que serve de ponto de partida: a que está no ar, porque é
     * dela que se replica para as demais. Nenhuma ativa, vale a primeira.
     */
    principal() {
      return this.ordenadas.find((integracao) => integracao.ativo) || this.ordenadas[0];
    },
  },
  methods: {
    corDoEstado(integracao) {
      if (!integracao.ativo) {
        return '#d0d5dd';
      }

      return integracao.status_conexao === 'FALHA' ? '#d92d20' : '#1c7a44';
    },
  },
};
</script>

<style scoped>
.grupo__contagem {
  padding: 2px 10px;
  border-radius: 12px;
  background: #eceaf3;
  color: #3a3e81;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.finalidades {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
}

.finalidade {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 6px;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.finalidade:hover {
  background: #f7f7fb;
}

.finalidade--inativa .finalidade__nome {
  color: #98a2b3;
}

.finalidade__ponto {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.finalidade__nome {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.finalidade__aviso {
  flex-shrink: 0;
  font-size: 11px;
  color: #98a2b3;
}

.finalidade--inativa .finalidade__aviso {
  color: #98a2b3;
}

.finalidade__aviso .fa-exclamation-triangle {
  color: #d92d20;
}
</style>
